import { registrarItemsDelMenu, menuItemDe } from '../utils/menuItem'
import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../api/client'
import AsistenteChat from './AsistenteChat'
import CommandPalette from './CommandPalette'
import ModalCambiarPassword from './ModalCambiarPassword'
import AvisoEmergente from './avisos/AvisoEmergente'
import AvisoVersionNueva from './AvisoVersionNueva'
import PanelAvisos from './avisos/PanelAvisos'
import { avisosApi } from '../api/avisos'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { confirmarSiSucio } from '../hooks/useDirtyGuard'

// Catálogo del menú (estructura + labels). La VISIBILIDAD por rol se configura en
// SISTEMA → Menú por rol y se sirve desde /api/menu-permisos. El administrador ve todo.
// La sección SISTEMA es adminOnly (solo el administrador, no configurable).
// Menú propio de la empresa EMP 900 (se muestra en lugar de SECTIONS cuando
// la ruta está bajo /emp900). Mismo esqueleto que LGR (Martin, 25 ago: "el
// ítem ventas tiene que tener nv, fac, rec, rem al igual que lgr"). Cada
// ítem se autoriza por separado en Menú por rol: así se habilita el
// circuito A (Facturas + Depósito), el B (Notas de venta) o los dos.
// EMP 900 APAGADA (Martin, 15 oct 2026: "sacá emp900 que por el momento no se
// usa"): sin botón en el sidebar ni bloque en Menú por rol. Nada se borró — las
// rutas /emp900 y sus permisos guardados siguen; para volver, `true`.
export const EMP900_ACTIVA = false

export const EMP900_SECCIONES = [
  { id: 'ventas', label: 'VENTAS', items: [
    { path: '/emp900/notas-venta', label: 'Notas de venta', disabled: true },   // circuito B (LISTA1) — próximo
    { path: '/emp900/facturas', label: 'Facturas' },                           // circuito A (LISTALM)
    { path: '/emp900/recibos', label: 'Recibos' },
    { path: '/emp900/remitos', label: 'Remitos' },
  ] },
  { id: 'deposito', label: 'DEPÓSITO', items: [
    { path: '/emp900/deposito', label: 'Facturas a remitir', disabled: true },  // espera remito↔factura en el ERP
  ] },
  { id: 'compras', label: 'COMPRAS', items: [{ path: '/emp900/compras', label: 'Compras' }] },
  { id: 'cuentas', label: 'CUENTAS CORRIENTES', items: [{ path: '/emp900/cuentas', label: 'Cuentas corrientes' }] },
  { id: 'stock', label: 'STOCK', items: [{ path: '/emp900/stock', label: 'Stock' }] },
]

export const SECTIONS = [
  // Orden del menú: Martin, 15 oct 2026 (Ventas, Clientes, Comunicaciones,
  // Experiencias, Marketing, Stock, Compras, Finanzas, BI, Logística, RRHH, Sistema).
  {
    id: 'ventas', label: 'VENTAS', icon: '🧾',
    items: [
      { path: '/notas-venta', label: 'Notas de Venta' },
      { path: '/facturas', label: 'Facturas' },
      { path: '/remitos', label: 'Remitos' },
      { path: '/recibos', label: 'Recibos' },
      { path: '/presupuestos', label: 'Presupuestos' },
      { path: '/gift-cards', label: 'Gift cards' },
      { path: '/wine-club/composicion', label: 'Wine Club' },
    ],
  },
  {
    id: 'clientes', label: 'CLIENTES', icon: '👥',
    items: [
      { path: '/clientes', label: 'Clientes' },
      { path: '/clientes/historia', label: 'Historia de clientes' },
      { path: '/clientes/deudores', label: 'Deudores' },
      { path: '/clientes/lista-negra', label: 'Lista negra' },
      { path: '/clientes/portal', label: 'Portal' },
      { path: '/clientes/wine-club', label: 'Wine Club' },
      { path: '/clientes/resenas', label: 'Reseñas' },
      { path: '/clientes/cuentas-web', label: 'Cuentas web' },
    ],
  },
  // COMUNICACIONES — WhatsApp como canal de LGR (bandeja multiusuario, Fase 1+2,
  // docs/diseno-whatsapp-lgr.md). Como todo ítem, se habilita por rol en Menú
  // por rol; el backend además exige WHATSAPP_INBOX_ENABLED.
  {
    id: 'comunicaciones', label: 'COMUNICACIONES', icon: '💬',
    items: [
      { path: '/comunicaciones/whatsapp', label: 'WhatsApp' },
      { path: '/comunicaciones/whatsapp/equipos', label: 'Equipos y etiquetas' },
    ],
  },
  // EXPERIENCIAS — sección propia (Martin, 15 oct 2026): las experiencias de la
  // web y los informes de eventos, que estaban en Stock.
  {
    id: 'experiencias', label: 'EXPERIENCIAS', icon: '🎟',
    items: [
      { path: '/web/experiencias', label: 'Experiencias (web)' },
      { path: '/eventos/informes', label: 'Informes de eventos' },
    ],
  },
  // MARKETING — vuelve el 25 sep 2026 con Próxima Selección (Martin); el 15 oct
  // 2026 suma Carritos abandonados, Propuestas y Sommelier IA (de Ventas) y
  // Cazador (de Sistema).
  // Lo viejo (Campañas, Automations, Listas, Plantillas, Contactos) sigue
  // oculto desde el 7 jun hasta que se defina el rediseño
  // (docs/diseno_marketing_crm.md); sus rutas /marketing/* siguen vivas por
  // URL directa.
  {
    id: 'marketing', label: 'MARKETING', icon: '📣',
    items: [
      // Fuera de /marketing/: nginx sirve esa ruta como imágenes de campañas
      // (Brevo) y una recarga daba 404.
      { path: '/proxima-seleccion', label: 'Próxima Selección' },
      { path: '/segunda-compra', label: 'Segunda compra' },
      { path: '/recompra', label: 'Recompra' },
      { path: '/regalos-empresariales', label: 'Regalos empresariales' },
      { path: '/carritos-abandonados', label: 'Carritos abandonados' },
      { path: '/newsletter-experiencias', label: 'Newsletter Experiencias' },
      { path: '/propuestas-venta', label: 'Propuestas' },
      { path: '/ventas/sommelier', label: 'Sommelier IA' },
      { path: '/configuracion/cazador', label: 'Cazador' },
    ],
  },
  {
    id: 'stock', label: 'STOCK', icon: '📦',
    items: [
      { path: '/articulos', label: 'Artículos' },
      { path: '/listas-precios', label: 'Listas de Precios' },
      { path: '/promociones', label: 'Promociones' },
      { path: '/stock/mas-esperados', label: 'Los más esperados' },
      { path: '/stock/consultas-deposito', label: 'Consultas a depósito' },
      { path: '/stock/fotos-pendientes', label: 'Fotos pendientes' },
      { path: '/stock/inventario', label: 'Inventario' },
      { path: '/clientes/resenas-productos', label: 'Reseñas de productos' },
    ],
  },
  // COMPRAS — se ejecuta en LGR (Martin, 15 sep 2026). Pedidos (Nota de
  // Pedido): las cinco operaciones son ítems propios, como en Presea; cada una
  // se autoriza por separado en Menú por rol (camino de Martin, 2 sep 2026).
  {
    id: 'compras', label: 'COMPRAS', icon: '🛒',
    items: [],
    subsections: [
      {
        id: 'pedidos', label: 'Pedidos',
        items: [
          { path: '/compras/pedidos/ingreso', label: 'Ingreso de pedidos' },
          { path: '/compras/pedidos/modificacion', label: 'Modificación' },
          { path: '/compras/pedidos/anulacion', label: 'Anulación' },
          { path: '/compras/pedidos/cancelacion', label: 'Cancelación' },
          { path: '/compras/pedidos/consultas', label: 'Consultas' },
        ],
      },
    ],
  },
  {
    id: 'finanzas', label: 'FINANZAS', icon: '💰',
    items: [
      { path: '/finanzas/pagos', label: 'Pagos varios' },
      { path: '/finanzas/pagos-proveedores', label: 'Pago a proveedores' },
      { path: '/finanzas/cobranzas', label: 'Cobranzas' },
      { path: '/finanzas/minutas', label: 'Minutas' },
      { path: '/finanzas/conciliacion-tarjetas', label: 'Conciliación de tarjetas' },
      { path: '/valores', label: 'Valores' },
    ],
    subsections: [
      {
        id: 'finanzas-maestros', label: 'Maestros',
        items: [
          { path: '/cuentas-contables', label: 'Cuentas contables' },
          { path: '/finanzas/bancos-presea', label: 'Formas de pago Presea' },
        ],
      },
    ],
  },
  // BI reactivado (1 jul 2026, módulo BI potente): Tablero de Dirección con motor
  // de hallazgos + drill-downs Ventas/Gastos. El cubo de junio (/bi/cubo) sigue
  // fuera del menú; "Cubo" (/bi/cubo-ventas) es el nuevo al estilo del cubo de
  // Presea — período primero, todos los campos, cálculos sobre la vista (4 oct 2026).
  // Ver docs/diseno-bi-potente.md.
  {
    id: 'bi', label: 'BUSINESS INTELLIGENCE', icon: '📈',
    items: [
      { path: '/bi', label: 'Tablero' },
      { path: '/bi/ventas', label: 'Ventas' },
      { path: '/bi/gastos', label: 'Gastos' },
      { path: '/bi/cubo-ventas', label: 'Cubo' },
      { path: '/bi/reportes', label: 'Reportes' },
    ],
  },
  {
    id: 'logistica', label: 'LOGÍSTICA', icon: '🚛',
    items: [
      { path: '/logistica/remitos', label: 'Remitos' },
      { path: '/asignaciones', label: 'Asignaciones' },
      { path: '/logistica/distribucion', label: 'Distribución' },
      { path: '/logistica/retiros', label: 'Retiros por sucursal' },
      { path: '/casos', label: 'Casos de entrega' },
      { path: '/logistica/historial', label: 'Historial' },
      { path: '/informes', label: 'Tablero' },
    ],
    subsections: [
      {
        id: 'maestros', label: 'Maestros',
        items: [
          { path: '/zonas', label: 'Zonas' },
          { path: '/fleteros', label: 'Fleteros' },
          { path: '/motivos-no-entrega', label: 'Motivos No Entrega' },
        ],
      },
    ],
  },
  ...(EMP900_ACTIVA ? [{
    // EMP 900 — OTRA EMPRESA (no una sección del menú). Se entra con el botón
    // "EMP 900" del sidebar (pestaña nueva), que solo ven los autorizados;
    // adentro el menú es EMP900_SECCIONES y el color primario pasa a verde.
    // Esta entrada existe para que SISTEMA → Menú por rol otorgue cada ítem
    // de la 900 por separado (circuito A, B o los dos); el sidebar la saltea
    // (`empresa: true`). Backend: middleware `acceso-menu:/emp900`.
    id: 'emp900', label: 'EMP 900 (empresa)', icon: '🏢', empresa: true,
    items: EMP900_SECCIONES.flatMap(s => s.items.map(i => ({ path: i.path, label: `${s.label} · ${i.label}` }))),
  }] : []),
  {
    // Confidencial — solo Super Administrador (superAdminOnly).
    id: 'rrhh', label: 'RRHH', icon: '🔒', superAdminOnly: true,
    items: [
      { path: '/rrhh/sueadic', label: 'SueAdic' },
      { path: '/rrhh/sueadic/estadisticas', label: 'Estadísticas' },
      { path: '/rrhh/bonus', label: 'Bonus' },
      { path: '/procesos/entrevistas', label: 'Relevamiento IA' },
    ],
    subsections: [
      {
        id: 'rrhh-maestros', label: 'Maestros',
        items: [
          { path: '/rrhh/pagadores', label: 'Pagadores' },
        ],
      },
    ],
  },
  {
    // SISTEMA es configurable por rol como el resto, MENOS sus cuatro LLAVES
    // (`llave: true`), que quedan siempre solo-admin y no aparecen en el menú
    // por rol. Se llaman llaves porque quien entra puede darse a sí mismo todo
    // lo demás, o leer lo que no le corresponde:
    //   · Menú por rol → se auto-habilita cualquier sección.
    //   · Roles        → cambia qué puede cada rol.
    //   · Usuarios     → crea un administrador nuevo.
    //   · Tablas       → consola SQL sobre TODA la base; es de solo lectura,
    //                    pero lee los sueldos de SueAdic, que es confidencial
    //                    de super-admin. Sería una puerta lateral a eso.
    id: 'sistema', label: 'SISTEMA', icon: '⚙️',
    items: [
      { path: '/usuarios', label: 'Usuarios', llave: true },
      { path: '/configuracion/roles', label: 'Roles', llave: true },
      { path: '/configuracion/menu', label: 'Menú por rol', llave: true },
      { path: '/sistema/tablas', label: 'Tablas', llave: true },
      // Solo super admin (Martin, 29 sep 2026: "solo yo"); el backend lo cierra con `superadmin`.
      { path: '/sistema/mercadolibre', label: 'MercadoLibre', superAdminOnly: true },
      { path: '/web/traducciones', label: 'Traducciones web' },
      { path: '/web/filtros', label: 'Filtros de la tienda' },
      { path: '/configuracion/automata', label: 'Autómata' },
      { path: '/configuracion/correos', label: 'Correos' },
      { path: '/logs-actividad', label: 'Log de Actividad' },
      { path: '/configuracion/sugerencias', label: 'Sugerencias' },
      // Asistente LGR 2 (4 oct 2026): administradores + personas habilitadas (Diego Gorfinkiel).
      { path: '/sistema/asistente-conversaciones', label: 'Conversaciones del bot' },
    ],
    subsections: [
      {
        id: 'sistema-presea', label: 'ERP Presea',
        items: [
          { path: '/integraciones/presea/cola', label: 'Cola' },
          { path: '/integraciones/presea/maestros', label: 'Maestros' },
        ],
      },
    ],
  },
]

// Catálogo de ítems para saber de qué pantalla sale cada pedido (permisos por persona).
registrarItemsDelMenu([
  ...SECTIONS.flatMap(s => [...(s.items || []), ...(s.subsections || []).flatMap(sub => sub.items || [])]).map(i => i.path),
  ...EMP900_SECCIONES.flatMap(s => s.items).map(i => i.path),
])

// Los roles imitables se leen de /roles (SISTEMA → Roles): la lista fija que
// había acá se quedó vieja cuando "ventas" pasó a ser "ventas call" y
// aparecieron "ventas sucursal" y "marketing" — el super admin no podía
// mirar el sistema con los ojos de los roles nuevos (ago 2026).
// `administrador` no se imita: ya ve todo.
const etiquetaRol = (nombre) => String(nombre).charAt(0).toUpperCase() + String(nombre).slice(1)

export default function Layout({ children }) {
  const { user, realUser, logout, hasRole, isSuperAdmin, impersonateRole, impersonateUser, setImpersonate, setImpersonateUsuario, actualizarUsuario } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // En una pantalla de celular el layout de escritorio es incómodo: se ofrece
  // la versión móvil (/m) con un banner, sin forzar la redirección.
  const [esPantallaChica] = useState(() => window.innerWidth <= 640)
  const esDev = typeof window !== 'undefined' && (window.location.hostname.startsWith('dev.') || window.location.hostname === 'localhost')

  // Roles que el super admin puede imitar. Se piden a /roles (el endpoint es
  // solo-admin) y solo si es super admin: para el resto no aplica el selector.
  const [rolesImitables, setRolesImitables] = useState([])
  const [usuariosImitables, setUsuariosImitables] = useState([])
  useEffect(() => {
    if (!isSuperAdmin || impersonateRole || impersonateUser) return
    api.get('/roles')
      .then(({ data }) => setRolesImitables(
        (data.data || []).map(r => r.name).filter(n => n !== 'administrador'),
      ))
      .catch(() => setRolesImitables([]))
    // "Ver como" una persona (Martin, 3 sep 2026): la vista exacta de un
    // usuario puntual — su rol, su lista, sus NVs propias.
    api.get('/usuarios')
      .then(({ data }) => setUsuariosImitables(
        (data.data || data || []).filter(u => u.activo !== false
          && u.rol !== 'super_administrador' && u.id !== realUser?.id),
      ))
      .catch(() => setUsuariosImitables([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, impersonateRole, impersonateUser])

  // Visibilidad del menú según el rol (configurable; admin ve todo)
  const [menu, setMenu] = useState({ todo: false, items: new Set(), restringidos: new Set(), mios: new Set(), cargado: false })
  useEffect(() => {
    api.get('/menu-permisos/mias')
      .then(({ data }) => setMenu({ todo: !!data.todo, items: new Set(data.items || []), restringidos: new Set(data.restringidos || []), mios: new Set(data.mios || []), cargado: true }))
      .catch(() => setMenu({ todo: false, items: new Set(), restringidos: new Set(), mios: new Set(), cargado: true }))
  }, [])

  // Flag PRESEA_FAKE — refrescamos por /me al montar para que usuarios
  // logueados de antes del cambio también vean el banner sin re-login.
  const [preseaFake, setPreseaFake] = useState(() => !!realUser?.presea_fake)
  const [cambiarPass, setCambiarPass] = useState(false)
  useEffect(() => {
    api.get('/me')
      .then(({ data }) => {
        setPreseaFake(!!data.presea_fake)
        // Rol/roles frescos del server: un cambio de rol se refleja sin
        // re-login (AuthContext ignora esto mientras se imita un rol).
        actualizarUsuario(data)
      })
      .catch(() => { /* sin red: dejamos el valor inicial */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Capa por PERSONA (28 sep 2026): un ítem restringido a personas lo ve solo
  // quien está en su lista (el backend ya incluye al super admin en `mios`),
  // aunque el rol lo tenga o sea administrador.
  const puedeVer = (path) => menu.restringidos.has(path) ? menu.mios.has(path) : (menu.todo || menu.items.has(path))

  // Una pantalla restringida a otras personas (link directo, o el backend la
  // rechazó): en lugar de la pantalla, "No tenés acceso".
  const itemActual = menuItemDe(location.pathname)
  const [rechazada, setRechazada] = useState(null)
  useEffect(() => {
    const on = () => setRechazada(location.pathname)
    window.addEventListener('lgr:sin-acceso', on)
    return () => window.removeEventListener('lgr:sin-acceso', on)
  }, [location.pathname])
  const sinAcceso = (menu.cargado && itemActual && menu.restringidos.has(itemActual) && !menu.mios.has(itemActual))
    || rechazada === location.pathname

  // EMP 900 — otra empresa. modo900 = la ruta está adentro; el color primario
  // pasa a verde vía data-empresa (index.css). Puede operarla el super admin
  // real y cualquier rol con el permiso "Operar EMP 900" en Menú por rol;
  // el "todo" del administrador NO alcanza (regla de Martin: solo autorizados).
  const modo900 = location.pathname.startsWith('/emp900')
  const ver900 = (path) => (isSuperAdmin && !impersonateRole && !impersonateUser) || menu.items.has(path)
  const puede900 = EMP900_ACTIVA && ((isSuperAdmin && !impersonateRole && !impersonateUser)
    || [...menu.items].some(k => k === '/emp900' || k.startsWith('/emp900/')))
  useEffect(() => {
    if (modo900) document.documentElement.dataset.empresa = '900'
    else delete document.documentElement.dataset.empresa
    return () => { delete document.documentElement.dataset.empresa }
  }, [modo900])

  // AVISOS — mensajería interna. El poll (30s, pausa con pestaña oculta)
  // alimenta la ventana emergente (sin_leer: bloquea hasta "✓ Leído") y la
  // campanita del sidebar (abiertos: leídos pero no resueltos).
  const [avisos, setAvisos] = useState({ sin_leer: [], abiertos: [] })
  const [panelAvisos, setPanelAvisos] = useState(false)
  const cargarAvisos = useCallback(() => {
    avisosApi.mios().then(({ data }) => setAvisos({
      sin_leer: data.sin_leer || [],
      abiertos: data.abiertos || [],
    })).catch(() => {})
  }, [])
  useEffect(() => { cargarAvisos() }, [cargarAvisos])
  useAutoRefresh(cargarAvisos, 30000)
  const avisosAbiertos = avisos.sin_leer.length + avisos.abiertos.length

  // Un ítem del menú puede llevar un contador ámbar (render genérico más
  // abajo). Hoy no lo usa nadie: el de CLIENTES → Wine Club (cobros que Mobbex
  // no pudo hacer, pedido del 31 ago 2026) se sacó el 2 sep 2026 porque un
  // número suelto al lado del ítem no decía qué era y confundía — ese aviso
  // llega por Telegram, y el detalle vive en la ficha del socio.
  const badgesMenu = {}

  // Secciones abiertas del menú — LA VISTA SE GUARDA (pedido de Martin,
  // 22 ago 2026): lo que cada uno deja abierto o cerrado con sus clicks queda
  // en su navegador (useStoredState, el mismo mecanismo que los filtros de los
  // listados) y sobrevive a navegación, F5 y sesiones. Sin nada guardado
  // todavía, cae al comportamiento de siempre: abrir la sección de la página
  // en la que está parado.
  const [openSections, setOpenSections] = useStoredState('lgr.menu.abiertas', null)
  // Buscador de ítems del menú (Martin, 15 oct 2026).
  const [buscarMenu, setBuscarMenu] = useState('')
  const seccionesVisibles = openSections ?? (() => {
    const open = {}
    SECTIONS.forEach(s => {
      const allItems = [...s.items, ...(s.subsections?.flatMap(ss => ss.items) || [])]
      if (allItems.some(item => !item.disabled && location.pathname.startsWith(item.path))) {
        open[s.id] = true
      }
      s.subsections?.forEach(ss => {
        if (ss.items.some(item => location.pathname.startsWith(item.path))) {
          open[`${s.id}-${ss.id}`] = true
        }
      })
    })
    return open
  })()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggle = (id) => setOpenSections({ ...seccionesVisibles, [id]: !seccionesVisibles[id] })

  const isActive = (path) =>
    path !== '/dashboard' && location.pathname.startsWith(path)

  // Qué ítems ve esta persona: UNA regla para el sidebar y para el buscador.
  const seccionVisible = (section) => !(section.adminOnly && !hasRole('administrador'))
    && !(section.superAdminOnly && !isSuperAdmin)
    && !section.empresa
    && !(section.id === 'compras' && !(realUser || user)?.compras_np)
  const itemVisible = (item) => (!item.llave || hasRole('administrador'))
    && (!item.superAdminOnly || isSuperAdmin)
    && puedeVer(item.path)
  const sinTildes = (t) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const qMenu = sinTildes(buscarMenu.trim())
  const resultadosMenu = !qMenu ? [] : SECTIONS.filter(seccionVisible).flatMap(sec => [
    ...sec.items.map(i => ({ ...i, donde: sec.label })),
    ...(sec.subsections || []).flatMap(sub => sub.items.map(i => ({ ...i, donde: `${sec.label} › ${sub.label}` }))),
  ]).filter(i => !i.disabled && itemVisible(i) && sinTildes(`${i.label} ${i.donde}`).includes(qMenu))

  return (
    <div style={st.wrapper}>
      <aside style={st.sidebar}>
        <div style={st.logo}>LGR</div>

        {modo900 ? (
          <>
            <div style={st.empresaChip}>🏢 EMP 900</div>
            <Link to="/dashboard" style={st.volverBtn} onClick={(e) => { if (!confirmarSiSucio()) e.preventDefault() }}>← Volver a LGR</Link>
            <div style={st.divider} />
            <nav style={st.nav}>
              {EMP900_SECCIONES.map(sec => {
                const visibles = sec.items.filter(i => ver900(i.path))
                if (visibles.length === 0) return null
                return (
                  <div key={sec.id}>
                    <div style={st.sectionLabel900}>{sec.label}</div>
                    {visibles.map(item => item.disabled ? (
                      <span key={item.path} style={st.itemDisabled} title="Próximamente">{item.label} <span style={st.proximamente}>próximamente</span></span>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={(e) => { if (!confirmarSiSucio()) e.preventDefault() }}
                        style={{ ...st.navItem900, ...(isActive(item.path) ? st.navItemActive : {}) }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )
              })}
            </nav>
          </>
        ) : (
          <>
            <Link
              to="/dashboard"
              style={{
                ...st.dashLink,
                ...(location.pathname === '/dashboard' ? st.dashLinkActive : {}),
              }}
            >
              🏠 Dashboard
            </Link>

            <button style={st.avisosBtn} onClick={() => setPanelAvisos(true)}>
              <span>🔔 Avisos</span>
              {avisosAbiertos > 0 && <span style={st.avisosBadge}>{avisosAbiertos}</span>}
            </button>

            <div style={st.divider} />

            <input
              style={st.buscarMenu}
              value={buscarMenu}
              onChange={e => setBuscarMenu(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setBuscarMenu('')
                if (e.key === 'Enter' && resultadosMenu[0] && confirmarSiSucio()) { navigate(resultadosMenu[0].path); setBuscarMenu('') }
              }}
              placeholder="Buscar…"
              aria-label="Buscar en el menú"
            />

            <nav style={st.nav}>
              {qMenu && (
                resultadosMenu.length === 0
                  ? <span style={st.comingSoon}>Nada con «{buscarMenu.trim()}»</span>
                  : resultadosMenu.map(item => (
                    <Link key={item.path} to={item.path}
                      onClick={(e) => { if (!confirmarSiSucio()) e.preventDefault(); else setBuscarMenu('') }}
                      style={{ ...st.navItem, paddingLeft: '1.25rem', ...(isActive(item.path) ? st.navItemActive : {}) }}>
                      <span style={{ display: 'block' }}>{item.label}</span>
                      <span style={st.buscarDonde}>{item.donde}</span>
                    </Link>
                  ))
              )}
              {!qMenu && SECTIONS.map(section => {
                // Interruptor COMPRAS_NP (Martin, 15 sep 2026) y demás reglas de
                // sección: seccionVisible (la misma que usa el buscador).
                if (!seccionVisible(section)) return null
                const verItem = puedeVer

                // Las LLAVES del sistema (Usuarios, Roles, Menú por rol, Tablas)
                // son siempre solo-admin, sin importar lo que diga el menú por rol:
                // habilitarlas equivale a entregar el sistema entero.
                const visibleItems = section.items.filter(itemVisible)
                const visibleSubsections = (section.subsections || [])
                  .map(sub => ({ ...sub, visibles: sub.items.filter(item => verItem(item.path)) }))
                  .filter(sub => sub.visibles.length > 0)
                // Si el rol no tiene NINGÚN item accesible en la sección (ni en sus
                // subsections), ocultamos el título entero — no tiene a dónde entrar.
                const totalVisibles = visibleItems.length
                  + visibleSubsections.reduce((acc, s) => acc + s.visibles.length, 0)
                if (totalVisibles === 0) return null
                const hasContent = visibleItems.some(item => !item.disabled)
                const isOpen = seccionesVisibles[section.id]

                return (
                  <div key={section.id}>
                    <button
                      style={st.sectionHeader}
                      onClick={() => toggle(section.id)}
                    >
                      <span style={st.sectionIcon}>{section.icon}</span>
                      <span style={st.sectionLabel}>{section.label}</span>
                      <span style={st.arrow}>{isOpen ? '▾' : '▸'}</span>
                    </button>

                    {isOpen && (
                      <div style={st.sectionItems}>
                        {visibleItems.length === 0 || !hasContent ? (
                          <span style={st.comingSoon}>Próximamente</span>
                        ) : (
                          visibleItems.map(item =>
                            item.disabled ? (
                              <span key={`${section.id}-${item.path}`} style={st.itemDisabled}>
                                {item.label}
                              </span>
                            ) : (
                              <Link
                                key={`${section.id}-${item.path}`}
                                to={item.path}
                                onClick={(e) => { if (!confirmarSiSucio()) e.preventDefault() }}
                                style={{
                                  ...st.navItem,
                                  ...(isActive(item.path) ? st.navItemActive : {}),
                                }}
                              >
                                {item.label}
                                {badgesMenu[item.path]?.n > 0 && (
                                  <span style={st.itemBadge} title={badgesMenu[item.path].title}>{badgesMenu[item.path].n}</span>
                                )}
                              </Link>
                            )
                          )
                        )}
                        {visibleSubsections.map(sub => {
                          const subKey = `${section.id}-${sub.id}`
                          const isSubOpen = seccionesVisibles[subKey]
                          return (
                            <div key={subKey}>
                              <button style={st.subHeader} onClick={() => toggle(subKey)}>
                                <span style={st.subLabel}>{sub.label}</span>
                                <span style={st.arrow}>{isSubOpen ? '▾' : '▸'}</span>
                              </button>
                              {isSubOpen && sub.visibles.map(item => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={(e) => { if (!confirmarSiSucio()) e.preventDefault() }}
                                  style={{
                                    ...st.navItem,
                                    paddingLeft: '3rem',
                                    ...(isActive(item.path) ? st.navItemActive : {}),
                                  }}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>
            {puede900 && (
              // Pestaña NUEVA (Martin, 25 ago): la 900 es otra empresa y se
              // trabaja al lado de LGR, no en lugar de LGR. La sesión viaja
              // sola (token en localStorage, mismo origen).
              <a href="/emp900/facturas" target="_blank" rel="noopener" style={st.empresaBtn}>
                🏢 EMP 900 ↗
              </a>
            )}
          </>
        )}

        <div style={st.userSection}>
          <div style={st.userName}>{user?.nombre}</div>
          <div style={st.userRole}>{isSuperAdmin ? 'Super Administrador' : user?.rol}</div>

          {isSuperAdmin && (
            <div style={{ marginBottom: '0.625rem' }}>
              <label style={st.verComoLabel}>👁 Ver como</label>
              <select
                value={impersonateUser ? `u:${impersonateUser.id}` : (impersonateRole || '')}
                onChange={e => {
                  const v = e.target.value
                  if (v.startsWith('u:')) {
                    const u = usuariosImitables.find(x => String(x.id) === v.slice(2))
                    setImpersonateUsuario(u || { id: Number(v.slice(2)), nombre: 'usuario' })
                  } else {
                    setImpersonate(v || null)
                  }
                }}
                style={st.verComoSelect}
              >
                <option value="">Super Admin (yo)</option>
                {rolesImitables.length > 0 && (
                  <optgroup label="Un rol">
                    {rolesImitables.map(r => <option key={r} value={r}>{etiquetaRol(r)}</option>)}
                  </optgroup>
                )}
                {(usuariosImitables.length > 0 || impersonateUser) && (
                  <optgroup label="Una persona">
                    {impersonateUser && !usuariosImitables.some(u => u.id === impersonateUser.id) && (
                      <option value={`u:${impersonateUser.id}`}>{impersonateUser.nombre}</option>
                    )}
                    {usuariosImitables.map(u => <option key={u.id} value={`u:${u.id}`}>{u.nombre}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
          )}

          <button style={st.passBtn} onClick={() => navigate('/m')}>📱 Versión móvil</button>
          <button style={st.passBtn} onClick={() => setCambiarPass(true)}>Cambiar mi contraseña</button>
          <button style={st.passBtn} onClick={() => navigate('/seguridad')}>Seguridad (segundo factor)</button>
          <button style={st.logoutBtn} onClick={handleLogout}>Cerrar sesión</button>
          {cambiarPass && <ModalCambiarPassword onClose={() => setCambiarPass(false)} />}
        </div>
      </aside>

      <main style={st.main}>
        {/* Entorno de DESARROLLO (dev.lgr.vinotecaligier.com): misma app, otra
            base y otra rama; los mails, Presea, Magento y Telegram están
            apagados por .env. El banner evita cargar "de verdad" acá. */}
        {esDev && (
          <div style={st.devBanner}>
            🧪 <b>ENTORNO DE DESARROLLO</b> — lo que hagas acá no toca producción (base de prueba, integraciones apagadas). Producción: <a href="https://lgr.vinotecaligier.com" style={{ color: 'inherit' }}>lgr.vinotecaligier.com</a>
          </div>
        )}
        {esPantallaChica && (
          <div style={st.movilBanner}>
            <span>📱 Pantalla chica detectada — hay una versión pensada para el celular.</span>
            <button style={st.movilBtn} onClick={() => navigate('/m')}>Abrir versión móvil →</button>
          </div>
        )}
        {preseaFake && (
          <div style={st.fakeBanner}>
            <span>🧪 <b>PRESEA — MODO PRUEBA.</b> Las facturas, remitos y recibos enviados a Presea son <b>simulados</b>: no llegan al ERP real. Los pedidos de Magento siguen importándose normalmente.</span>
          </div>
        )}
        {impersonateRole && (
          <div style={st.impBanner}>
            <span>👁 Estás viendo el sistema como <b>{etiquetaRol(impersonateRole)}</b> (con sus restricciones).</span>
            <button style={st.impBtn} onClick={() => setImpersonate(null)}>← Volver a Super Admin</button>
          </div>
        )}
        {impersonateUser && (
          <div style={st.impBanner}>
            <span>👁 Estás viendo el sistema como <b>{impersonateUser.nombre}</b> — su menú, su lista y sus ventas. Ojo: lo que cargues queda a su nombre.</span>
            <button style={st.impBtn} onClick={() => setImpersonateUsuario(null)}>← Volver a Super Admin</button>
          </div>
        )}
        {sinAcceso ? <SinAcceso /> : children}
      </main>

      <AsistenteChat />
      <CommandPalette />
      <AvisoVersionNueva />

      {/* Ventana EMERGENTE de avisos: el canal garantizado — bloquea hasta
          tocar "✓ Leído". El panel de la campanita muestra los abiertos.
          "Viendo como" un usuario NO bloquea (Martin, 7 sep 2026): el acuse
          es de esa persona; el super admin los ve en la campanita, sin marcar. */}
      {!impersonateUser && avisos.sin_leer.length > 0 && (
        <AvisoEmergente
          avisos={avisos.sin_leer}
          onLeido={(id) => setAvisos(prev => {
            const leido = prev.sin_leer.find(a => a.id === id)
            return {
              sin_leer: prev.sin_leer.filter(a => a.id !== id),
              abiertos: leido ? [...prev.abiertos, { ...leido, leido_at: new Date().toISOString() }] : prev.abiertos,
            }
          })}
        />
      )}
      {panelAvisos && (
        <PanelAvisos
          avisos={[...avisos.sin_leer, ...avisos.abiertos]}
          onResuelto={(id) => setAvisos(prev => ({
            sin_leer: prev.sin_leer.filter(a => a.id !== id),
            abiertos: prev.abiertos.filter(a => a.id !== id),
          }))}
          soloLectura={impersonateUser ? impersonateUser.nombre : null}
          onClose={() => setPanelAvisos(false)}
        />
      )}
    </div>
  )
}

const badgePendiente = { background: '#f59e0b', color: '#1e293b', borderRadius: '999px', minWidth: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, padding: '0 5px' }

const st = {
  wrapper: { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  sidebar: { width: '240px', minWidth: '240px', background: '#1e293b', color: 'white', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' },
  logo: { fontSize: '1.5rem', fontWeight: '800', padding: '1rem 1.25rem 0.75rem', color: 'var(--lgr-logo)', letterSpacing: '2px' },
  dashLink: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', fontSize: '0.875rem', color: '#94a3b8', textDecoration: 'none', borderRadius: 0 },
  avisosBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', fontSize: '0.875rem', color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: 'inherit' },
  // Un solo "pendiente para vos" (ámbar): la campanita de Avisos y los ítems
  // del menú con contador (badgesMenu, hoy vacío). Es el MISMO objeto a
  // propósito: así el ámbar del menú y el de la campanita no se desincronizan.
  // El #f59e0b no es token porque vive solo acá; sobre el sidebar oscuro el
  // amber-600 de colors.warnStrong pierde contraste.
  itemBadge: { ...badgePendiente, marginLeft: 8, verticalAlign: 'middle' },
  avisosBadge: { ...badgePendiente },
  dashLinkActive: { background: 'var(--lgr-primary)', color: 'white', fontWeight: '600' },
  divider: { height: '1px', background: '#334155', margin: '0.25rem 0' },
  buscarMenu: { margin: '0.5rem 1rem 0.25rem', padding: '0.45rem 0.65rem', fontSize: '0.8125rem', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontFamily: 'inherit', outline: 'none' },
  buscarDonde: { display: 'block', fontSize: '0.6875rem', color: '#64748b', letterSpacing: '0.03em' },
  nav: { flex: 1, overflowY: 'auto', padding: '0.25rem 0' },
  sectionHeader: { display: 'flex', alignItems: 'center', width: '100%', padding: '0.5rem 1rem 0.5rem 1.125rem', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textAlign: 'left', gap: '0.5rem' },
  sectionIcon: { fontSize: '0.875rem', flexShrink: 0 },
  sectionLabel: { flex: 1, fontWeight: '700', fontSize: '0.6875rem', letterSpacing: '0.08em', color: '#64748b' },
  arrow: { fontSize: '0.625rem', color: '#475569' },
  sectionItems: { paddingBottom: '0.25rem' },
  navItem: { display: 'block', padding: '0.4rem 1rem 0.4rem 2.25rem', fontSize: '0.8125rem', color: '#cbd5e1', textDecoration: 'none' },
  navItemActive: { background: 'var(--lgr-primary)', color: 'white', fontWeight: '600' },
  // EMP 900 — otra empresa: botón de entrada (solo autorizados), chip de
  // contexto y menú plano; el verde viene de --lgr-* (index.css).
  empresaBtn: { display: 'block', margin: '0.5rem 1rem 0.25rem', padding: '0.5rem 0.75rem', fontSize: '0.8125rem', fontWeight: 700, color: '#4ade80', border: '1px solid #15803d', borderRadius: 6, textDecoration: 'none', textAlign: 'center', letterSpacing: '0.04em' },
  empresaChip: { margin: '0 1.25rem 0.5rem', padding: '0.35rem 0.6rem', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', color: '#052e16', background: '#4ade80', borderRadius: 6, textAlign: 'center' },
  volverBtn: { display: 'block', margin: '0 1rem 0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.8125rem', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 6, textDecoration: 'none', textAlign: 'center' },
  navItem900: { display: 'block', padding: '0.4rem 1rem 0.4rem 2.25rem', fontSize: '0.8125rem', color: '#cbd5e1', textDecoration: 'none' },
  sectionLabel900: { padding: '0.6rem 1rem 0.25rem 1.125rem', fontWeight: '700', fontSize: '0.6875rem', letterSpacing: '0.08em', color: '#64748b' },
  proximamente: { fontSize: '0.65rem', color: '#475569', fontStyle: 'italic' },
  itemDisabled: { display: 'block', padding: '0.4rem 1rem 0.4rem 2.25rem', fontSize: '0.8125rem', color: '#475569', cursor: 'default' },
  comingSoon: { display: 'block', padding: '0.4rem 1rem 0.4rem 2.25rem', fontSize: '0.75rem', color: '#475569', fontStyle: 'italic' },
  subHeader: { display: 'flex', alignItems: 'center', width: '100%', padding: '0.35rem 1rem 0.35rem 2rem', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textAlign: 'left', gap: '0.4rem' },
  subLabel: { flex: 1, fontWeight: '600', fontSize: '0.6875rem', letterSpacing: '0.06em', color: '#475569', textTransform: 'uppercase' },
  userSection: { padding: '0.875rem 1.25rem', borderTop: '1px solid #334155' },
  userName: { fontWeight: '600', fontSize: '0.8125rem', marginBottom: '1px', color: '#e2e8f0' },
  userRole: { color: '#64748b', fontSize: '0.75rem', marginBottom: '0.625rem' },
  passBtn: { width: '100%', padding: '0.4rem', marginBottom: 6, background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' },
  logoutBtn: { width: '100%', padding: '0.4rem', background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' },
  verComoLabel: { display: 'block', fontSize: '0.68rem', color: '#94a3b8', marginBottom: 3, fontWeight: 700, letterSpacing: '0.03em' },
  verComoSelect: { width: '100%', padding: '0.35rem', background: '#0f172a', color: '#e2e8f0', border: '1px solid #475569', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer' },
  main: { flex: 1, padding: '1.5rem', overflowY: 'auto' },
  impBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', padding: '0.6rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.875rem' },
  movilBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', background: 'var(--lgr-link-bg)', border: '1px solid var(--lgr-link-border)', color: 'var(--lgr-primary)', padding: '0.6rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.875rem' },
  devBanner: { background: '#fef3c7', border: '1px solid #f59e0b', color: '#78350f', padding: '0.5rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' },
  movilBtn: { background: 'var(--lgr-primary)', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' },
  impBtn: { background: '#92400e', color: 'white', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' },
  fakeBanner: { background: '#fef3c7', border: '1px solid #f59e0b', color: '#78350f', padding: '0.7rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.875rem', lineHeight: 1.5 },
}

function SinAcceso() {
  return (
    <div style={{ maxWidth: 520, margin: '64px auto', textAlign: 'center', color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
      <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden="true">🔒</div>
      <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#0f172a' }}>No tenés acceso a esta pantalla</h2>
      <p style={{ margin: 0 }}>Está habilitada solo para algunas personas. Si la necesitás, pedile acceso al Super Administrador.</p>
    </div>
  )
}
